<template>
  <AppForm
    ref="form"
    :rules="rules"
    :values="formData"
    v-slot:default="{ formErrors }"
  >
    <div class="p-fluid p-formgrid p-grid">
      <div class="p-field p-col-12 p-lg-6">
        <AppFormItem label="Tipo" :error="formErrors.typeId">
          <AppAutoComplete
            v-model="formData.typeId"
            valueField="id"
            :options="types"
          ></AppAutoComplete>
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-lg-6">
        <AppFormItem label="Categoria">
          <AppAutoComplete
            v-model="formData.categoryId"
            valueField="id"
            :options="categories"
            :externalSeach="true"
            @search="onSearchCategory($event)"
          ></AppAutoComplete>
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-lg-6">
        <AppFormItem label="Nome">
          <InputText v-model="formData.name" />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-lg-6">
        <AppFormItem :error="formErrors.value" label="Valor">
          <InputNumber
            v-model="formData.value"
            :minFractionDigits="2"
            mode="currency"
            currency="BRL"
            locale="pt-BR"
          />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-lg-6">
        <AppFormItem label="Data" :error="formErrors.date">
          <Calendar
            v-model="formData.date"
            dateFormat="dd/mm/yy"
            showIcon
            :monthNavigator="true"
            :yearNavigator="true"
            :yearRange="dateRange"
            :showOnFocus="false"
            v-mask="'##/##/####'"
          />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-lg-6">
        <AppFormItem label="Responsável">
          <InputText v-model="formData.responsibleName" />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12" v-if="isVetAccount">
        <VetCustomerAutocomplete
          v-model:customer="formData.customer"
          v-model:customerId="formData.customerId"
          :disabled="!!$route.params.id"
          @update:customerId="handleCustomerSelect($event)"
        ></VetCustomerAutocomplete>
      </div>
      <div class="p-field p-col-12" v-if="!isVetAccount">
        <VetAutocomplete
          v-model:vet="formData.vet"
          v-model:vetId="formData.vetId"
          :disabled="!!$route.params.id"
        ></VetAutocomplete>
      </div>
      <div class="p-field p-col-12" v-if="!isVetAccount || formData.customerId">
        <AppFormItem label="Fazenda">
          <AppAutoComplete
            v-model="formData.farmId"
            valueField="id"
            :options="farms"
            :externalSeach="true"
            @search="onSearchFarm($event)"
          ></AppAutoComplete>
        </AppFormItem>
      </div>
      <div class="p-field p-col-12">
        <AppFormItem label="Etiquetas">
          <TagAutocomplete v-model="formData.tags" :typeId="2" />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12">
        <AppFormItem label="Descrição">
          <Textarea v-model="formData.description" rows="3" />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12" style="margin-bottom: 0">
        <div class="p-card-subtitle p-text-bold">Anexos</div>
      </div>
      <div class="p-field p-col-12">
        <FileDataTable
          :files="formData.attachments"
          @remove="onRemoveAttachment($event)"
        ></FileDataTable>
      </div>
      <div class="p-col-12">
        <AppUploadButton
          label="Adicionar Anexos"
          @upload="onUpload($event)"
          :maxSize="5 * 1024 * 1024"
          :loading="loadingFile"
        ></AppUploadButton>
      </div>
    </div>
  </AppForm>
</template>

<script>
import { mapGetters } from 'vuex';
import FileDataTable from '../../../components/FileDataTable.vue';
import TagAutocomplete from '../../../components/TagAutocomplete.vue';
import {
  CashFlowCategoryApi,
  CashFlowTransactionApi,
  FarmApi,
  VetCustomerApi,
} from '../../../services/api';
import VetCustomerAutocomplete from '../../../components/VetCustomerAutocomplete.vue';
import VetAutocomplete from '../../../components/VetAutocomplete.vue';

export default {
  name: 'CashFlowForm',
  components: {
    TagAutocomplete,
    FileDataTable,
    VetCustomerAutocomplete,
    VetAutocomplete,
  },
  data() {
    const getDateRange = () => {
      const start = this.$dayjs().subtract(10, 'y').format('YYYY');
      const end = this.$dayjs().add(10, 'y').format('YYYY');
      return `${start}:${end}`;
    };
    return {
      rules: {
        typeId: [{ required: true, message: 'Informe o tipo.' }],
        value: [{ required: true, message: 'Informe o valor.' }],
        date: [{ required: true, message: 'Informe a data.' }],
      },
      types: [
        { id: 1, name: 'Receita' },
        { id: 2, name: 'Despesa' },
      ],
      categories: [],
      farms: [],
      formData: {
        typeId: 1,
        categoryId: null,
        farmId: null,
        name: '',
        value: null,
        responsibleName: null,
        description: null,
        date: new Date(),
        tags: [],
        attachments: [],
        customer: null,
        customerId: null,
        vet: null,
        vetId: null,
      },
      dateRange: getDateRange(),
      loadingFile: false,
    };
  },
  computed: {
    ...mapGetters('app', ['isVetAccount']),
  },
  methods: {
    async setData(data) {
      this.formData = {
        ...this.formData,
        ...data,
      };
      if (data.date) {
        this.formData.date = new Date(data.date);
      }
      if (data.category) {
        this.categories = [data.category];
      }
      if (data.farm) {
        this.farms = [data.farm];
      }
      if (data.customerId) {
        const loginData = await VetCustomerApi.login({
          accountId: data.customerId,
        }).catch(() => {});
        this.formData.customer.accessToken = loginData.accessToken;
      }
    },
    async onUpload(event) {
      try {
        this.loadingFile = true;
        const requests = event.files.map((file) => {
          const formData = new FormData();
          formData.append(`file`, file);
          return CashFlowTransactionApi.uploadAttachment(formData);
        });
        const files = await Promise.all(requests);
        this.formData.attachments = [
          ...files.map((file) => ({
            fileId: file.id,
            file: { ...file, createdAt: new Date() },
          })),
          ...this.formData.attachments,
        ];
      } catch (error) {
        console.log(error);
        let detail = 'Não foi possível adicionar os anexos.';
        this.$toast.add({
          severity: 'error',
          summary: 'Desculpe!',
          detail,
          life: 3000,
        });
      } finally {
        this.loadingFile = false;
      }
    },
    onRemoveAttachment(event) {
      this.formData.attachments = this.formData.attachments.filter(
        (attachment) => attachment.fileId !== event
      );
    },
    async onSearchCategory(event) {
      const fetchResponse = await CashFlowCategoryApi.fetch({
        name: event.query || undefined,
      });
      this.categories = fetchResponse.results;
    },
    async onSearchFarm(event) {
      const fetchResponse = await FarmApi.fetch(
        {
          name: event.query || undefined,
        },
        { accessToken: this.formData?.customer?.accessToken }
      );
      this.farms = fetchResponse.results;
    },
    async submit() {
      if (!(await this.$refs.form.validate())) {
        return;
      }
      return this.formData;
    },
    async handleCustomerSelect(customerId) {
      try {
        this.loading = true;
        if (customerId) {
          const loginData = await VetCustomerApi.login({
            accountId: customerId,
          });
          this.formData.customer.accessToken = loginData.accessToken;
        }
        this.formData = {
          ...this.formData,
          farm: null,
          farmId: null,
        };
      } catch (error) {
        console.log(error);
        let detail = 'Não foi possível carregar os dados do cliente.';
        this.$toast.add({
          severity: 'error',
          summary: 'Desculpe!',
          detail,
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.p-galleria-thumbnail-wrapper {
  max-height: 30px;
}
</style>